import { SmartTableColumn, WithNAFallback } from '@toggle/design-system';
import { formatPercentage, getCurrencySymbol } from '@toggle/helpers';
import { FinancialData, formatTicker } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AssetLogoContainer } from '~/components/asset-logo-container/AssetLogoContainer';
import { useGetEarningById } from '~/hooks/use-get-earning-by-id/useGetEarningById';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { formatNumberWithUnit } from '~/widgets/earnings/utils/utils';
import { RatingPill } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/rating-pill/RatingPill';
import { SharedTable } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/shared-table/SharedTable';

import { EarningsPill } from '../EarningsPill';
import { CompanyCardProps } from './company-card-types';
import * as S from './CompanyCard.styles';

export const PrimaryCompanyCard = ({ company, entity }: CompanyCardProps) => {
  const { t } = useTranslation(['earnings']);
  const { navigateToPage } = useNavigateToAssetOverview();

  const earningsId = company.earnings_id;
  const { data: earningsData, isLoading } = useGetEarningById(earningsId);

  const reportingDate = earningsData?.reporting_date;
  const isReported = !!earningsData?.reported;
  const eps = earningsData?.eps;
  const revenue = earningsData?.revenue;

  if (isLoading) {
    return (
      <S.Root>
        <S.StyledLoader />
      </S.Root>
    );
  }

  const tableData = [
    {
      name: t('earnings:earningsInsight.drawer.earningsPreview.tables.eps'),
      key: 'eps',
      ...eps,
    },
    {
      name: t('earnings:earningsInsight.drawer.earningsPreview.tables.revenue'),
      key: 'revenue',
      ...revenue,
    },
  ];

  const tableColumns: SmartTableColumn<
    FinancialData & { name: string; key: string }
  >[] = [
    {
      label: t('earnings:earningsInsight.drawer.earningsPreview.tables.name'),
      key: 'name',
      renderHeader: () => {
        return null;
      },
      render: value => <S.TableNameCell>{value}</S.TableNameCell>,
    },
    {
      label: t(
        'earnings:earningsInsight.drawer.earningsPreview.tables.estimate'
      ),
      key: 'estimate',
      alignment: 'right',
      render: value => {
        const currencySymbol = getCurrencySymbol(entity.currency);
        return (
          <WithNAFallback
            check={value !== undefined && value !== null}
            value={
              <S.NumberCell>{`${currencySymbol}${formatNumberWithUnit(
                value
              )}`}</S.NumberCell>
            }
          />
        );
      },
    },
    {
      label: t(
        'earnings:earningsInsight.drawer.earningsPreview.tables.reported'
      ),
      key: 'value',
      alignment: 'right',
      render: value => {
        const currencySymbol = getCurrencySymbol(entity.currency);
        return (
          <WithNAFallback
            check={value !== undefined && value !== null}
            value={
              <S.NumberCell>{`${currencySymbol}${formatNumberWithUnit(
                value
              )}`}</S.NumberCell>
            }
          />
        );
      },
    },
    {
      label: t(
        'earnings:earningsInsight.drawer.earningsPreview.tables.surprise'
      ),
      key: 'surprise',
      alignment: 'right',
      render: value => {
        const currencySymbol = getCurrencySymbol(entity.currency);
        return (
          <WithNAFallback
            value={
              value !== undefined && (
                <S.SurpriseCell
                  $isPositive={value > 0}
                  $isZero={value === 0}
                >{`${currencySymbol}${formatNumberWithUnit(
                  value
                )}`}</S.SurpriseCell>
              )
            }
          />
        );
      },
    },
    {
      label: t(
        'earnings:earningsInsight.drawer.earningsPreview.tables.changeYoy'
      ),
      key: 'estimate_yoy_percentage',
      alignment: 'right',
      render: value => (
        <WithNAFallback
          value={
            value !== undefined && (
              <S.SurpriseCell $isPositive={value > 0} $isZero={value === 0}>
                {formatPercentage(value, {
                  suffix: '%',
                  addPlus: true,
                  useOriginalValue: true,
                })}
              </S.SurpriseCell>
            )
          }
        />
      ),
    },
  ];

  return (
    <S.Root onClick={() => navigateToPage({ entityTag: company.entity_tag })}>
      <S.HeaderWrapper>
        <S.AssetDetailsWrapper>
          <AssetLogoContainer size={40} entity={entity} />
          <S.AssetNameWrapper>
            <S.AssetNameLabel>
              {entity.name_short || entity.name}
            </S.AssetNameLabel>
            <S.AssetTickerLabel>{formatTicker(entity)}</S.AssetTickerLabel>
          </S.AssetNameWrapper>
        </S.AssetDetailsWrapper>
        <RatingPill rating={company.rating} />
      </S.HeaderWrapper>
      {!!reportingDate && (
        <EarningsPill
          entity={entity}
          isReported={isReported}
          reportingDate={reportingDate}
        />
      )}
      {isReported ? (
        <SharedTable
          tableData={tableData}
          tableColumns={tableColumns}
          noCellPadding
        />
      ) : (
        <S.PotentialImpactLabel>
          {t('earnings:earningsInsight.drawer.earningsPreview.potentialImpact')}
        </S.PotentialImpactLabel>
      )}
      <S.DescriptionLabel $isReported={isReported}>
        {company.description}
      </S.DescriptionLabel>
      {company.relationship && (
        <S.RelationLabel>{`${t(
          'earnings:earningsInsight.drawer.earningsPreview.relationship'
        )} ${company.relationship}`}</S.RelationLabel>
      )}
    </S.Root>
  );
};
